// React imports
import { useEffect, useState } from 'react';

// Generic imports
import _ from 'lodash';
import { formatDisplayDate } from '../../utils/date.js';
import axios from 'axios';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';

// MUI Imports
import { Box, Typography, Paper, Chip, TextField, FormControl, InputLabel, Select, CircularProgress, FormControlLabel,
    MenuItem, Checkbox, ListItemText, ListSubheader, Slider, Button, Alert, Popover, Autocomplete, OutlinedInput, Switch } from '@mui/material';
import { blue } from '@mui/material/colors';
import PRODUCT_TYPES from '../../ProductTypes.js';

// Local imports
import Where from '../../Where.js';

function Filters(props) {
    const { transportationMethod, seaPorts, suppliers, onFilter, 
        minValidFrom, maxValidFrom, minValidUntil, maxValidUntil,
        filters, accesstoken, org, unlocode,
    } = props;
    const [ sources, setSources ] = useState([]);
    const [ destinations, setDestinations ] = useState([]);
    const [ incEverywhere, setIncEverywhere ] = useState(false);
    const [ products, setProducts ] = useState([]);
    const [ validFrom, setValidFrom ] = useState([0, 100]);
    const [ validUntil, setValidUntil ] = useState([0, 100]);
    const [ filterSuppliers, setFilterSuppliers ] = useState([]);
    const [ suppliersListOpen, setSuppliersListOpen ] = useState(false);
    const [ suppliersInputValue, setSuppliersInputValue ] = useState('');
    const [ showAISearch, setShowAISearch ] = useState(false);
    const [ aiFunction, setAIFunction ] = useState(null);
    const [ aiThinking, setAIThinking ] = useState(false);
    const [ errors, setErrors ] = useState([]);

    useEffect(() => {
        onFilter({ ...filters, sources, destinations, products, suppliers: filterSuppliers, aiFunction, incEverywhere });
    }, [sources, destinations, products, filterSuppliers, aiFunction, incEverywhere]);

    useEffect(() => {
        setValidFrom([minValidFrom, maxValidFrom]);
    }, [ minValidFrom, maxValidFrom ]);

    useEffect(() => {
        setValidUntil([minValidUntil, maxValidUntil]);
    }, [ minValidUntil, maxValidUntil ]);

    function onDeleteSource({ s }) {
        setSources(_.without(sources, s));
    }

    function onDeleteDestination({ s }) {
        setDestinations(_.without(destinations, s));
    }

    async function onTextSearch(value) {
        try {
            setErrors([]);
            setAIThinking(true);
            const { data } = await axios.post(`/api/rates/generate-filter?accesstoken=${accesstoken}&organizationId=${org.id}`, { search: value+`. Filter for rates with transportationMethod=${transportationMethod} only.` });
            console.log('Function returned', data);
            const func = new Function('rates', 'getCostValue', data);
            /*
            const filtered = func(_.cloneDeep(allRates), getCostValue);
            console.log('Filtered results', filtered);

            if (filtered.length === 0) {
                setErrors(['textsearch-noresults']);
            } else if (filtered.length > 100) {
                setErrors(['textsearch-toomanyresults']);
            } else {
            }
            */
            setAIThinking(false);
            setAIFunction(data);
        } catch (e) {
            setAIThinking(false);
            setErrors(['textsearch']);
            console.error(e);
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 2 }}>
                <Typography variant='body2' sx={{ paddingRight: '12px' }}>
                    Filters:
                </Typography>
                <BasicPopover 
                    title={(sources.length + destinations.length) === 0 ? 'Origin & Destination' : `Picked ${sources.length} origin${sources.length.length !== 1 ? 's' : ''} and ${destinations.length} destination${destinations.length.length !== 1 ? 's' : ''}`}
                    sx={{ marginRight: '10px' }}
                    buttonVariant={(sources.length + destinations.length) === 0 ? 'outlined' : 'contained'}
                 >
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', width: '400px' }}>
                            <Where
                                transportationMethod={transportationMethod}
                                errors={errors}
                                setSource={(v) => { setSources([...sources, v]) }}
                                setDestination={(v) => { setDestinations([...destinations, v]) }}
                                seaPorts={seaPorts}
                                unlocode={unlocode}
                                nogmaps={transportationMethod !== 'land'}
                                organization={org}
                                accesstoken={accesstoken}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', width: '400px' }}>
                            <Box sx={{ flex: 1, padding: '3px', minWidth: '235px', maxWidth: '235px' }}>
                                {sources.map(s => ( 
                                    <Chip 
                                        key={s.text} 
                                        label={s.text} 
                                        sx={{ margin: '3px' }} 
                                        onDelete={() => onDeleteSource({s})} 
                                    />
                                ))}
                            </Box>
                            <Box sx={{ flex: 1, padding: '3px', minWidth: '235px', maxWidth: '235px' }}>
                                {destinations.map(s => ( 
                                    <Chip 
                                        key={s.text} 
                                        label={s.text}
                                        sx={{ margin: '3px' }} 
                                        onDelete={() => onDeleteDestination({s})}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <FormControlLabel control={<Switch size='small' checked={incEverywhere} onChange={() => setIncEverywhere(!incEverywhere)}/>} label={<Typography variant='caption'>Include "Everywhere".</Typography>} />
                    </Paper>
                </BasicPopover>
                {suppliers && (
                    <BasicPopover 
                        title={filterSuppliers.length === 0 ? 'Suppliers' : `Picked ${filterSuppliers.length} supplier${filterSuppliers.length > 1 ? 's' : ''}`}
                        sx={{ marginRight: '10px' }}
                        buttonVariant={filterSuppliers.length === 0 ? 'outlined' : 'contained'}
                     >
                        <Paper sx={{ p: 2 }}>
                            <Autocomplete
                                multiple
                                disablePortal
                                options={_(suppliers)
                                    .compact()
                                    .sortBy(a => _.trim(a).toUpperCase())
                                    .filter(a => !filterSuppliers.includes(a))
                                    .value()
                                }
                                sx={{ minWidth: 300, flex: 1 }}
                                renderInput={(params) => (
                                    <TextField
                                        className='black-placeholder' 
                                        {...params} 
                                        placeholder={_.isEmpty(filterSuppliers) ? 'All Suppliers' : ''} 
                                        inputProps={{ ...params.inputProps, style: { paddingTop: '8px', paddingBottom: '8px', color: 'black' } }} 
                                    />
                                )}
                                value={ filterSuppliers }
                                onChange={(e, v) => { 
                                    setSuppliersListOpen(true);
                                    setFilterSuppliers(v);
                                }}
                                open={suppliersListOpen}
                                onOpen={() => {
                                    setSuppliersListOpen(true);
                                }}
                                onClose={(e, reason) => {
                                    if (reason !== 'selectOption') {
                                        setSuppliersListOpen(false);
                                        setSuppliersInputValue('');
                                    }
                                }}
                                inputValue={suppliersInputValue}
                                onInputChange={(e, v, reason) => {
                                    if (reason === 'input') {
                                        setSuppliersInputValue(v);
                                    }
                                    if (reason === 'clear') {
                                        setSuppliersInputValue('');
                                    }
                                }}
                            />
                        </Paper>
                    </BasicPopover>
                )}
                <BasicPopover 
                    title={products.length === 0 ? 'Products' : `Picked ${products.length} product${products.length > 1 ? 's' : ''}`}
                    sx={{ marginRight: '10px' }}
                    buttonVariant={products.length === 0 ? 'outlined' : 'contained'}
                 >
                    <Paper sx={{ p: 2 }}>
                        <FormControl sx={{ margin: '0px 6px 0px 0px', width: 300 }}>
                            <InputLabel id='products-label'>Products</InputLabel>
                            <Select
                                multiple
                                error={errors.includes('products')}
                                value={products}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setProducts(typeof value === 'string' ? value.split(',') : value);
                                }}
                                input={<OutlinedInput label='Products`' />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} onMouseDown={e => { if (typeof e.target.className !== 'string' || e.target.className.indexOf('MuiChip-label') === -1) { e.stopPropagation(); e.preventDefault();}}} onDelete={e => { 
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setProducts(p => _.without(p, value));
                                            }}/>
                                        ))}
                                    </Box>
                                )}
                                MenuProps={{
                                    sx: {
                                        zIndex: 100000000,
                                    },
                                    PaperProps: {
                                        style: {
                                            maxHeight: 48 * 4.5 + 8,
                                            width: 250,
                                        },
                                    }
                                }}
                            >
                                {transportationMethod === 'land' ? (
                                    /* Land: grouped (Containers / LCL / Trucks) to match the rate-search and
                                       opportunity product pickers. Filtering by `group` includes LCL and
                                       excludes the legacy no-group land types. */
                                    ['Containers', 'LCL', 'Air', 'Trucks'].flatMap(group => {
                                        const items = (PRODUCT_TYPES.land || []).filter(t => t.group === group);
                                        if (!items.length) return [];
                                        return [
                                            <ListSubheader key={`group-${group}`} sx={{ lineHeight: '32px' }}>{group}</ListSubheader>,
                                            ...items.map(product => (
                                                <MenuItem key={product.name} value={product.name}>
                                                    <Checkbox checked={products.indexOf(product.name) > -1} />
                                                    <ListItemText primary={product.name} sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
                                                </MenuItem>
                                            )),
                                        ];
                                    })
                                ) : (
                                    PRODUCT_TYPES[transportationMethod].map((product) => {
                                        if (product.name === 'By Dimensions or Weight (sea)') return null;
                                        if (product.name === 'By Volume or Weight (sea)') return null;
                                        if (product.name === 'LCL') return null;
                                        if (product.name === 'Air Freight') return null;
                                        return (
                                            <MenuItem key={product.name} value={product.name}>
                                                <Checkbox checked={products.indexOf(product.name) > -1} />
                                                <ListItemText primary={product.name} sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
                                            </MenuItem>
                                        )
                                    }).flat()
                                )}
                                {transportationMethod === 'sea' && (
                                    <MenuItem key='LCL' value='LCL'>
                                        <Checkbox checked={products.indexOf('LCL') > -1} />
                                        <ListItemText primary='LCL' sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Paper>
                </BasicPopover>
                <BasicPopover 
                    title={
                        (!filters.minValidFrom && !filters.maxValidFrom && !filters.minValidUntil && !filters.maxValidUntil) || 
                        (filters.minValidFrom === minValidFrom && 
                        filters.maxValidFrom === maxValidFrom && 
                        filters.minValidUntil === minValidUntil && 
                        filters.maxValidUntil === maxValidUntil) ?
                        'Validity' :
                        `Valid from ${filters.minValidFrom === minValidFrom && filters.maxValidFrom === maxValidFrom ? 'anytime' : `${new Date(filters.minValidFrom).toLocaleDateString('en-GB')} to ${new Date(filters.maxValidFrom).toLocaleDateString('en-GB')}`} and valid until ${filters.maxValidUntil === maxValidUntil && filters.minValidUntil === minValidUntil ? 'anytime' : `${new Date(filters.minValidUntil).toLocaleDateString('en-GB')} to ${new Date(filters.maxValidUntil).toLocaleDateString('en-GB')}`}`
                    }
                    sx={{ marginRight: '10px' }}
                    buttonVariant={ 
                        (!filters.minValidFrom && !filters.maxValidFrom && !filters.minValidUntil && !filters.maxValidUntil) || 
                        (filters.minValidFrom === minValidFrom && 
                        filters.maxValidFrom === maxValidFrom && 
                        filters.minValidUntil === minValidUntil && 
                        filters.maxValidUntil === maxValidUntil) ? 'outlined' : 'contained'}
                 >
                    <Paper sx={{ p: 2, width: '600px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='caption'>
                                Validity from:
                            </Typography>
                            {validFrom[0] === minValidFrom && validFrom[1] === maxValidFrom ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    Anytime
                                </Typography>
                            ) : validFrom[0] === validFrom[1] ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    exactly {formatDisplayDate(validFrom[0])}.
                                </Typography>
                            ) : validFrom[0] === minValidFrom ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    Anytime before {formatDisplayDate(validFrom[1])} (inclusive)
                                </Typography>
                            ) : validFrom[1] === maxValidFrom ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    Anytime after {formatDisplayDate(validFrom[0])} (inclusive)
                                </Typography>
                            ) : (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    {formatDisplayDate(validFrom[0])} - {formatDisplayDate(validFrom[1])}
                                </Typography>
                            )}
                        </Box>
                        <Slider
                            getAriaLabel={() => 'Valid from range'}
                            value={validFrom}
                            onChange={(e, v) => setValidFrom(v)}
                            valueLabelDisplay='auto'
                            getAriaValueText={''}
                            valueLabelFormat={(value) => formatDisplayDate(value)}
                            min={minValidFrom}
                            max={maxValidFrom}
                            step={DAY}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='caption'>
                                Validity until:
                            </Typography>
                            {validUntil[0] === minValidUntil && validUntil[1] === maxValidUntil ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    Anytime
                                </Typography>
                            ) : validUntil[0] === validUntil[1] ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    exactly {formatDisplayDate(validUntil[0])}.
                                </Typography>
                            ) : validUntil[0] === minValidUntil ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    Anytime before {formatDisplayDate(validUntil[1])} (inclusive)
                                </Typography>
                            ) : validUntil[1] === maxValidUntil ? (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    Anytime after {formatDisplayDate(validUntil[0])} (inclusive)
                                </Typography>
                            ) : (
                                <Typography variant='caption' sx={{ color: blue[500], marginLeft: '6px' }}>
                                    {formatDisplayDate(validUntil[0])} - {formatDisplayDate(validUntil[1])}
                                </Typography>
                            )}
                        </Box>
                        <Slider
                            getAriaLabel={() => 'Valid until range'}
                            value={validUntil}
                            onChange={(e, v) => setValidUntil(v)}
                            valueLabelDisplay='auto'
                            getAriaValueText={''}
                            valueLabelFormat={(value) => formatDisplayDate(value)}
                            min={minValidUntil}
                            max={maxValidUntil}
                            step={DAY}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
                            <Button variant='contained' onClick={() => onFilter({ 
                                ...filters,
                                minValidFrom: validFrom[0],
                                maxValidFrom: validFrom[1],
                                minValidUntil: validUntil[0],
                                maxValidUntil: validUntil[1]
                            })}>
                                Apply Filters
                            </Button>
                            <Button variant='text' onClick={() => {
                                setValidFrom([minValidFrom, maxValidFrom]);
                                setValidUntil([minValidUntil, maxValidUntil]);
                                onFilter({ ...filters, minValidFrom, maxValidFrom, minValidUntil, maxValidUntil });
                            }} sx={{ ml: 2 }}>
                                Reset Filters
                            </Button>
                        </Box>
                    </Paper>
                </BasicPopover>
                {!showAISearch && (
                    <Button variant='text' onClick={() => setShowAISearch(true)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }} >
                        Add AI Text Search
                    </Button>
                )}
            </Box>
            {showAISearch && transportationMethod && (
                <Box sx={{ display: 'flex', alignItems: 'center', pb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextSearchBox onSearch={onTextSearch} errors={errors} thinking={aiThinking} />
                    </Box>
                    <Button variant='text' onClick={() => { setShowAISearch(false); setAIFunction(null); }} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }} >
                        Remove AI Text Search        
                    </Button>
                </Box>
            )}
        </Box>
    )
}

function TextSearchBox(props) {
    const { onSearch, errors, thinking } = props;
    const [ value, setValue ] = useState('');

    function _onSearch() {
        onSearch(value);
    }

    return (
        <Box sx={{ p: 2 }} >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                    fullWidth
                    variant='outlined'
                    label='Search'
                    InputProps={{
                        style: { borderRadius: 0 }
                    }}
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && _onSearch()}
                />
                <Button
                    variant='contained'
                    sx={{ padding: '19px 0px',
                    borderRadius: 0 }}
                    onClick={_onSearch}
                    disabled={thinking}
                >
                    {thinking ? <CircularProgress size={18} /> : <FontAwesomeIcon icon={faSearch} />}
                </Button>
            </Box>
            {errors.includes('textsearch') && (
                <Alert severity='error'>
                    There was an error with your search. Please try again.
                </Alert>
            )}
        </Box>
    )
}

function BasicPopover(props) {
    const { title, sx, buttonVariant } = props;
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' + Math.random() : undefined;

    return (
        <Box sx={sx}>
            <Button aria-describedby={id} variant={buttonVariant} onClick={handleClick}>
                {title}
                {open ? <FontAwesomeIcon icon={faTimes} style={{ marginLeft: '6px' }} /> : <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '6px' }} />}
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{ paper: { sx: { overflow: 'visible' }}}}
            >
                {props.children}
            </Popover>
        </Box>
    );
}

const DAY = 1000 * 60 * 60 * 24;

export { Filters }
